import { useHydrated } from "@/hooks/useHydrated";
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acceptAll,
  rejectAll,
  savePreferences,
  initConsent,
  isConsentRequired,
  getConsentState,
  loadConsentedScripts,
} from '@/lib/consent';
import { PUBLIC_URLS } from '@/marketing/lib/public-urls';

interface CookieConsentCoreProps {
  authStateLoaded: boolean;
  isSignedIn: boolean;
}

/**
 * GDPR-compliant cookie consent banner.
 *
 * Appears as a fixed bottom bar when consent is required and the user
 * hasn't made a choice yet. Users can accept all, reject all, or open
 * a preferences panel to toggle analytics and marketing categories.
 */
export function CookieConsentCore({ authStateLoaded, isSignedIn }: CookieConsentCoreProps) {
  const hydrated = useHydrated();
  const [visibilityOverride, setVisible] = useState<boolean | null>(null);
  const visible = visibilityOverride ?? (hydrated && authStateLoaded && !isSignedIn && isConsentRequired() && !getConsentState());
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);

  useEffect(() => {
    if (!authStateLoaded) return;
    if (isSignedIn) {
      loadConsentedScripts({ analytics: true, marketing: true, updatedAt: '' });
      return;
    }
    initConsent();
  }, [authStateLoaded, isSignedIn]);

  useEffect(() => {
    const handler = () => {
      if (isSignedIn) return;
      const existing = getConsentState();
      if (existing) {
        setAnalyticsChecked(existing.analytics);
        setMarketingChecked(existing.marketing);
      }
      setShowPreferences(true);
      setVisible(true);
    };
    window.addEventListener('a7y:open-cookie-settings', handler);
    return () => window.removeEventListener('a7y:open-cookie-settings', handler);
  }, [isSignedIn]);

  // While the banner shows, publish its height as --a7y-consent-bar-height on
  // <html>, so a page's own bottom-anchored control can sit above it (A7Y-5604:
  // the More pill on /start).
  const bannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const banner = bannerRef.current;
    if (!visible || !banner) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--a7y-consent-bar-height', `${banner.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--a7y-consent-bar-height');
    };
  }, [visible]);

  const handleAcceptAll = useCallback(() => {
    acceptAll();
    setVisible(false);
    setShowPreferences(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    rejectAll();
    setVisible(false);
    setShowPreferences(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    savePreferences(analyticsChecked, marketingChecked);
    setVisible(false);
    setShowPreferences(false);
  }, [analyticsChecked, marketingChecked]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-secondary bg-white/95 p-4 shadow-lg backdrop-blur-sm dark:bg-gray-900/95 sm:p-6"
    >
      <div className="mx-auto max-w-4xl">
        {showPreferences ? (
          <PreferencesPanel
            analyticsChecked={analyticsChecked}
            marketingChecked={marketingChecked}
            onAnalyticsChange={setAnalyticsChecked}
            onMarketingChange={setMarketingChecked}
            onSave={handleSavePreferences}
            onAcceptAll={handleAcceptAll}
          />
        ) : (
          <BannerContent
            onAcceptAll={handleAcceptAll}
            onRejectAll={handleRejectAll}
            onManage={() => setShowPreferences(true)}
          />
        )}
      </div>
    </div>
  );
}

function BannerContent({
  onAcceptAll,
  onRejectAll,
  onManage,
}: {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onManage: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-foreground">
        We use cookies and similar technologies to improve your experience, analyze traffic, and
        support affiliate partnerships.{' '}
        <a
          href={PUBLIC_URLS.cookiePolicy}
          className="underline hover:text-emerald-700 dark:hover:text-emerald-400"
        >
          Cookie Policy
        </a>
      </p>
      <div className="flex shrink-0 flex-wrap gap-2">
        <button
          onClick={onRejectAll}
          className="rounded-md border border-secondary px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Reject All
        </button>
        <button
          onClick={onManage}
          className="rounded-md border border-secondary px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Manage Preferences
        </button>
        <button
          onClick={onAcceptAll}
          className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-800"
        >
          Accept All
        </button>
      </div>
    </div>
  );
}

function PreferencesPanel({
  analyticsChecked,
  marketingChecked,
  onAnalyticsChange,
  onMarketingChange,
  onSave,
  onAcceptAll,
}: {
  analyticsChecked: boolean;
  marketingChecked: boolean;
  onAnalyticsChange: (v: boolean) => void;
  onMarketingChange: (v: boolean) => void;
  onSave: () => void;
  onAcceptAll: () => void;
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground">Cookie Preferences</h3>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked
          disabled
          className="mt-0.5 h-4 w-4 rounded border-gray-300"
        />
        <div>
          <span className="text-sm font-medium text-foreground">Necessary</span>
          <p className="text-xs text-muted-foreground">
            Required for authentication, security, and core functionality. Cannot be disabled.
          </p>
        </div>
      </label>

      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={analyticsChecked}
          onChange={(e) => onAnalyticsChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
        />
        <div>
          <span className="text-sm font-medium text-foreground">Analytics</span>
          <p className="text-xs text-muted-foreground">
            Helps us understand how visitors use the site so we can improve it (Mixpanel).
          </p>
        </div>
      </label>

      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={marketingChecked}
          onChange={(e) => onMarketingChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
        />
        <div>
          <span className="text-sm font-medium text-foreground">Marketing</span>
          <p className="text-xs text-muted-foreground">
            Supports our affiliate program by setting a first-party referral cookie
            (a7y-aff, kept for 60 days) so partners get credit for visitors they send us.
            No third-party trackers.
          </p>
        </div>
      </label>

      <div className="flex flex-wrap gap-2 pt-2">
        <button
          onClick={onSave}
          className="rounded-md border border-secondary px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Save Preferences
        </button>
        <button
          onClick={onAcceptAll}
          className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-800"
        >
          Accept All
        </button>
      </div>
    </div>
  );
}
